import useFetcher from './useFetcher';
import { useOrganization } from 'app/_providers/Organization';

import { OrgRole, ProductRoleFull, WorkspaceRoleFull } from 'shared/src/authz/rolesV2';
import useSWR from 'swr';

const EMPTY_ARRAY: [] = [];

export type Invite = {
  id: string;
  recipient: string;
  recipientFirstName?: string;
  recipientLastName?: string;
  role: OrgRole;
  productRoles: ProductRoleFull[];
  workspaceRoles: WorkspaceRoleFull[];
  state: 'sent' | 'accepted' | 'revoked';
  expiration: string;
  created: string;
  creator: string;
  sender: string;
  updated: string;
  needsSignup?: boolean;
  invitationLink?: string;
  tenantId: string;
  teams?: {
    workspaceId: string;
    teamIds: string[];
  }[];
};

export type InviteAcceptedResponse = { location: string };

export type CreateInvite = {
  recipientEmail: string;
  recipientFirstName?: string;
  recipientLastName?: string;
  orgRole: OrgRole;
  workspaceRoles?: WorkspaceRoleFull[];
  productRoles?: ProductRoleFull[];
  teams?: {
    workspaceId: string;
    teamIds: string[];
  }[];
};

type InviteHook = {
  invites: Invite[];
  createInvite: (newInvite: Partial<CreateInvite>) => Promise<Invite>;
  resendInvite: (id: string) => Promise<void>;
  removeInvite: (id: string) => Promise<void>;
  acceptInvite: (code: string, tenantId: string) => Promise<InviteAcceptedResponse>;
  loadingInvites: boolean;
  errorInvites: Error | undefined;
};

const useInvites = (): InviteHook => {
  const { organizationId } = useOrganization();
  const { fetcher } = useFetcher();

  const v2url = `${process.env.ZEUS_API_URL}/v2/organizations/${organizationId}/invites`;
  const v1Url = `${process.env.ZEUS_API_URL}/v1/tenants`;

  const { data, mutate, isLoading, error } = useSWR<Invite[]>(
    organizationId ? v2url : null,
    fetcher,
  );

  const createInvite = async (newInvite: Partial<CreateInvite>): Promise<Invite> => {
    const invite = await fetcher<Invite>(v2url, {
      method: 'POST',
      body: JSON.stringify(newInvite),
    });
    mutate();

    return invite;
  };

  const resendInvite = async (id: string): Promise<void> => {
    await fetcher(`${v2url}/${id}/resend`, {
      method: 'POST',
    });
    mutate();
  };

  const acceptInvite = async (code: string, tenantId: string): Promise<InviteAcceptedResponse> => fetcher<InviteAcceptedResponse>(`${v1Url}/${tenantId}/invites/${code}`, {
    method: 'PATCH',
    body: JSON.stringify({ state: 'accepted' }),
  });

  const removeInvite = async (id: string): Promise<void> => {
    await fetcher(`${v2url}/${id}`, {
      method: 'DELETE',
    });
    mutate();
  };

  return {
    invites: data || EMPTY_ARRAY,
    createInvite,
    resendInvite,
    removeInvite,
    acceptInvite,
    loadingInvites: isLoading,
    errorInvites: error,
  };
};

export default useInvites;
